import { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { AlertTriangle, Search } from 'lucide-react';
import { useBaixarAtivo, useDocumentos, useAprovadoresBaixa } from '../hooks';
import { authService } from '../../../shared/lib/auth';
import { Button } from '../../../shared/components/Button';
import { Alert } from '../../../shared/components/Alert';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { RetirementType, UserLookupItem } from '../api';

interface Props {
  open: boolean;
  assetId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const RETIREMENT_LABELS: Record<RetirementType, string> = {
  disposal: 'Descarte',
  sale: 'Venda',
  loss: 'Perda',
  theft: 'Furto/roubo',
  donation: 'Doação',
};

const APPROVER_ROLE_LABEL: Record<string, string> = {
  admin: 'Administrador',
  governance: 'Governança',
  finance: 'Financeiro',
};

/**
 * Baixa patrimonial (SPEC 03).
 *
 * Etapa 1 — tipo + justificativa + anexo opcional.
 * Etapa 2 — seleção do aprovador (dropdown com busca em admin/governance/finance)
 *          + resumo + confirmação de irreversibilidade.
 *
 * Nesta fase o aprovador NÃO clica em nada; o executor declara. O backend
 * rejeita aprovador === executor (400).
 */
export function RetireModal({ open, assetId, onClose, onSuccess }: Props) {
  const mutation = useBaixarAtivo(assetId);
  const documentsQuery = useDocumentos(assetId);
  const currentUser = authService.getCurrentUser();

  const [step, setStep] = useState<1 | 2>(1);
  const [retirementType, setRetirementType] = useState<RetirementType>('disposal');
  const [justification, setJustification] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [approverSearch, setApproverSearch] = useState('');
  const [approver, setApprover] = useState<UserLookupItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const approversQuery = useAprovadoresBaixa(approverSearch);
  const candidateApprovers = (approversQuery.data?.data ?? []).filter(
    (u) => u.id !== currentUser?.id,
  );

  function reset() {
    setStep(1);
    setRetirementType('disposal');
    setJustification('');
    setDocumentId('');
    setApproverSearch('');
    setApprover(null);
    setError(null);
  }

  function handleNext(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (justification.trim().length < 10) {
      setError('Justificativa deve ter ao menos 10 caracteres.'); return;
    }
    setStep(2);
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!approver) { setError('Selecione o aprovador.'); return; }
    if (approver.id === currentUser?.id) {
      setError('O aprovador da baixa deve ser diferente de quem a executa.'); return;
    }
    try {
      await mutation.mutateAsync({
        justification: justification.trim(),
        approvedByUserId: approver.id,
        retirementType,
        documentId: documentId || undefined,
      });
      reset(); onSuccess(); onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } }).response?.data?.message;
      setError(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Falha ao baixar ativo'));
    }
  }

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
          <Dialog.Title className="flex items-center gap-2 text-lg font-semibold text-red-800">
            <AlertTriangle className="h-5 w-5" />
            Baixa patrimonial — etapa {step} de 2
          </Dialog.Title>

          {step === 1 && (
            <form onSubmit={handleNext} className="mt-4 space-y-3">
              <AutocompleteSelect
                label="Tipo de baixa *"
                options={(Object.entries(RETIREMENT_LABELS) as Array<[RetirementType, string]>).map(([v, l]) => ({ value: v, label: l }))}
                value={retirementType}
                onChange={(v) => setRetirementType(v as RetirementType)}
              />
              <Textarea
                label="Justificativa (mínimo 10 caracteres) *"
                rows={4}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                required
                minLength={10}
                showCharCount
              />
              <div>
                <AutocompleteSelect
                  label="Anexo (laudo, boletim, opcional)"
                  options={[
                    { value: '', label: 'Nenhum documento' },
                    ...(documentsQuery.data?.data ?? []).map((d) => ({ value: d.id, label: d.fileName })),
                  ]}
                  value={documentId}
                  onChange={setDocumentId}
                />
                <span className="mt-1 block text-xs text-gray-500">
                  Faça upload do laudo/BO na aba Anexos antes de selecionar aqui.
                </span>
              </div>

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => { reset(); onClose(); }}>Cancelar</Button>
                <Button variant="danger" type="submit">Avançar</Button>
              </div>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleConfirm} className="mt-4 space-y-3">
              <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                <p className="font-medium">Nesta fase, o aprovador não clica no sistema — você declara a aprovação.</p>
                <p className="mt-1">Somente perfis com permissão de baixa aparecem no dropdown. Não pode ser você mesmo.</p>
              </div>

              <div>
                <TextInput
                  label="Aprovador (nome ou e-mail, mín. 2 caracteres) *"
                  icon={<Search className="h-4 w-4" />}
                  value={approverSearch}
                  onChange={(e) => { setApproverSearch(e.target.value); setApprover(null); }}
                  autoComplete="off"
                />
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white">
                  {approversQuery.isLoading && (
                    <p className="p-2 text-xs text-gray-500">Buscando...</p>
                  )}
                  {approversQuery.isError && (
                    <p className="p-2 text-xs text-red-600">Falha ao buscar aprovadores.</p>
                  )}
                  {!approversQuery.isLoading && candidateApprovers.length === 0 && (
                    <p className="p-2 text-xs text-gray-500">
                      {approverSearch.length < 2 && approverSearch !== ''
                        ? 'Digite ao menos 2 caracteres para buscar.'
                        : 'Nenhum aprovador encontrado (excluindo você mesmo).'}
                    </p>
                  )}
                  {candidateApprovers.map((u) => (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => setApprover(u)}
                      className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-1.5 text-left text-sm hover:bg-blue-50 ${
                        approver?.id === u.id ? 'bg-blue-100' : ''
                      }`}
                    >
                      <span>
                        <span className="font-medium text-gray-900">{u.name}</span>
                        <span className="ml-2 text-xs text-gray-500">{u.email}</span>
                      </span>
                      <span className="text-xs text-gray-400">
                        {APPROVER_ROLE_LABEL[u.role] ?? u.role}
                      </span>
                    </button>
                  ))}
                </div>
                {approver && (
                  <p className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
                    Aprovador selecionado: <strong>{approver.name}</strong> ({APPROVER_ROLE_LABEL[approver.role] ?? approver.role})
                  </p>
                )}
              </div>

              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                <p><strong>Resumo:</strong></p>
                <p>Tipo: {RETIREMENT_LABELS[retirementType]}</p>
                <p>Justificativa: {justification}</p>
                {documentId && <p>Anexo: {documentsQuery.data?.data.find((d) => d.id === documentId)?.fileName}</p>}
              </div>
              <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-900">
                <strong>Atenção — operação irreversível.</strong> O ativo será marcado como <em>Baixado</em>. Movimentações, edição e entrega ficarão bloqueadas. Os dados permanecem consultáveis (RN004).
              </div>

              {error && <Alert variant="error">{error}</Alert>}

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="secondary" onClick={() => setStep(1)}>Voltar</Button>
                <Button variant="danger" type="submit" loading={mutation.isPending} disabled={!approver}>
                  {mutation.isPending ? 'Registrando…' : 'Confirmar baixa'}
                </Button>
              </div>
            </form>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
